import React from 'react';
import { connect } from 'react-redux';
import QueryString from 'query-string';
import _ from 'lodash';
import {CSVLink} from 'react-csv';
import cn from 'classnames';

import LANGUAGE_CONTROLLER from '../../controllers/languages';
import ApplicationMessages from '../inAppMessages/ApplicationMessages';
import AnalysisFilterPanel from './AnalysisFilterPanel';
import ExistingAnalysis from './ExistingAnalysis';
import GLOBAL_FILTER_CONTROLLER from '../../controllers/globalFilter';
import TRANSACTIONS_CONTROLLER from '../../controllers/transactions';
import TRANSACTION_EDIT_CONTROLLER from '../../controllers/transactionEdit';
import ANALYSIS_CONTROLLER from '../../controllers/analysis';
import {FIELDS as GLOBAL_FILTER_PANEL_FIELDS} from '../filter/GlobalFilterPanel';
import ComponentUtil from '../../utils/componentUtil';
import { sanitizeCsvData } from '../../utils/csvSanitizer';
import {AnalysisType, AnalysisTypeMainVATCodeOnly} from '../../utils/enums';
import AnalysisDetailsModal from './AnalysisDetailsModal';
import AnalysisDetails from './AnalysisDetails';
import ApplicationMessageWithDetail from '../inAppMessages/ApplicationMessageWithDetail';

import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../../utils/logger';
import ReactComponentHelper from '../helper/ReactComponentHelper';

const FORM_NAME = 'ANALYSIS';
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);

const PAGE_INTERNAL_IDS = [
    GLOBAL_FILTER_PANEL_FIELDS.PARAM_COMPANY_ID
];
const DEFAULT_START_AT = 1;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGES = -1;
const DEFAULT_TOTAL_COUNT = 0;

function requiredAPICall(params) {    
    let doNotCallAPI = _.isEmpty(params) || _.isEmpty(params.companyId+'') ||
    _.isEmpty(params.countryCode) ||
    _.isEmpty(params.endDate)  ||
    _.isEmpty(params.startDate) ||
    _.isEmpty(params.groupBy);
    return !doNotCallAPI;
}

class Analysis extends React.Component {

    constructor(props) {
        super(props);
        this.filterPanelSearchClickHandler = this.filterPanelSearchClickHandler.bind(this);
        this.getAnalysisBy = this.getAnalysisBy.bind(this);
        this.getFilterOptions = this.getFilterOptions.bind(this);
        this.getHeaders = this.getHeaders.bind(this);
        this.getInvoiceHeaders = this.getInvoiceHeaders.bind(this);
        this.openAnalysisDetailsModal = this.openAnalysisDetailsModal.bind(this);
        this.closeAnalysisDetailsModal = this.closeAnalysisDetailsModal.bind(this);
        this.fetchAnalysisDetails = this.fetchAnalysisDetails.bind(this);
        this.onAnalysisClickHandler = this.onAnalysisClickHandler.bind(this);
        this.onFetchInvoicesHandler = this.onFetchInvoicesHandler.bind(this);
        this.onRedeliverClickHandler = this.onRedeliverClickHandler.bind(this);
        this.onRedeliverSelectedVATCodesClickHandler = this.onRedeliverSelectedVATCodesClickHandler.bind(this);
        this.onVATCodesSelectionChange = this.onVATCodesSelectionChange.bind(this);
        this.HEADERS = {
            "vatCode":  LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_VATCode'),
            "genericVATCode": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_GenericVATCode'),
            "taxableBasis": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_TaxableBasis'),
            "vat": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_VAT'),
            "vatReversed": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_VATReversed'),
            "currency": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_Currency'),
            "company": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_Company'),
            "transaction": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_Transaction'),
            "ownComments": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_OwnComments'),
            "bookingInformation": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_BookingInformation'),
            "amount": LANGUAGE_CONTROLLER.format(props.languages, 'analysisReportField_Amount'),
        };
        this.ANALYSIS_DATA_CURRENCY_FIELDS =  [
            'taxableBasis',
            'vat',
            'vatReversed',
            'amount'
        ];
        this.INVOICE_CURRENCY_FIELDS =  [
            'valueExclVAT',
            'valueVAT',
            'reversedVAT'
        ];
        this.INVOICE_DATE_FIELDS =  [
            'invoiceDate'
        ];
        this.INVOICE_HEADERS ={ 
            "nr":  LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_Nr'),
            "invoiceNumber": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_InvoiceNumber'),
            "invoiceDate": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_InvoiceDate'),
            "documentType": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_DocumentType'),
            "sapDocumentType": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_SAPDocumentType'),
            "valueExclVAT": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_ValueExclVAT'),
            "valueVAT": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_ValueVAT'),
            "reversedVAT": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_ReversedVAT'),
            "currency": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_Currency'),
            "vatCode": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_VATCode'),
            "companyCode": LANGUAGE_CONTROLLER.format(props.languages, 'analysisDetails_CompanyCode'),
        };
        this.state = {
            analysisDetailsModalOpen: false,
            selectedVatCode: {},
            analysisDetailsLoading: false,
            invoicePagingOption: {
                totalCount: DEFAULT_TOTAL_COUNT,
                pages: DEFAULT_PAGES,
                pageSize: DEFAULT_PAGE_SIZE,
                startAt: DEFAULT_START_AT,
                filtered: [],
                sorted: []
            },
            resetSelection: false,
            selectedItems: new Set(),
            errorDetails: []
        };
    }

    openAnalysisDetailsModal() {
        log.debug('openAnalysisDetailsModal()');
        this.setState({
            analysisDetailsModalOpen: true
        });
    }

    closeAnalysisDetailsModal() {
        log.debug('closeAnalysisDetailsModal()');
        this.setState({
            analysisDetailsModalOpen: false,
            selectedVatCode: {}
        });
        this.props.doUnloadAnalysisDetails();
    }

    getAnalysisBy(value,  mainVATCodeOnly=false, key='languageString', defaultValue=AnalysisType.VAT_CODE) {
        log.debug('getAnalysisValueBy() - value: %s, key: %s, defaultValue: %s', value, key, defaultValue);
        let analysisTypeEnum = mainVATCodeOnly ? AnalysisTypeMainVATCodeOnly: AnalysisType;
        let matchedAnalysisType = analysisTypeEnum.enumValues.find( (analysisType) => {
            if(key === 'languageString') {
                return LANGUAGE_CONTROLLER.format(this.props.languages, analysisType[key])  === value;
            } else if(key === 'name') {
                return analysisType.name === value;
            }          
        });
        if(matchedAnalysisType) {
            return matchedAnalysisType;
        } else {
            if(mainVATCodeOnly) {
                return AnalysisTypeMainVATCodeOnly.VAT_CODE;
            } else {
                return defaultValue;
            }
        }
    }

    filterPanelSearchClickHandler(selection) {
        log.debug('filterPanelSearchClickHandler() - selection: ', selection);        
        let urlParams = Object.assign({}, selection);        
        urlParams.groupBy = selection.groupBy && selection.groupBy.name;
        ComponentUtil.updateUrlParams(this.props.location, this.props.history, urlParams, {removal: PAGE_INTERNAL_IDS});
        let apiParams = Object.assign({}, selection);
        if(requiredAPICall(apiParams)) {
            this.props.doLoadAnalysis(apiParams);
        } else {
            this.props.doUnloadAnalysis();
        }
        this.props.doClearAllAnalysisErrors();
    }

    getFilterOptions() {
        let globalFilterSelection = GLOBAL_FILTER_CONTROLLER.getAll();
        let filterOptions = Object.assign({}, globalFilterSelection, QueryString.parse(this.props.location.search)) ;                
        // Use 'name' key to match URL param (e.g., 'VAT_CODE_GENERIC') against enum name
        filterOptions.groupBy = this.getAnalysisBy(filterOptions.groupBy, filterOptions.mainVATCodesOnly === 'true', 'name');
        return filterOptions;
    }    

    getHeaders(groupBy=AnalysisType.VAT_CODE) {
        let headerKeys = Object.keys(this.HEADERS);
        switch(groupBy) {
            case AnalysisType.VAT_CODE:
            case AnalysisTypeMainVATCodeOnly.VAT_CODE:
                headerKeys = headerKeys.filter( key => !["bookingInformation", "amount"].includes(key));
                break;
            default:
        }
        return headerKeys.map( (headerKey) => {
            return {
                label: this.HEADERS[headerKey],
                key: headerKey
            };
        });
    }

    getInvoiceHeaders() {
        let headerKeys = Object.keys(this.INVOICE_HEADERS);
        return headerKeys.map( (headerKey) => {
            return {
                label: this.INVOICE_HEADERS[headerKey],
                key: headerKey
            };
        }); 
    }

    fetchAnalysisDetails(analysisData, pagingOption, callback) {
        log.debug('fetchAnalysisDetails(): analysisData: ', analysisData);
        analysisData = analysisData || {};
        pagingOption = pagingOption || {};
        const filterOptions = this.getFilterOptions();
        log.debug('fetchAnalysisDetails(): filterOptions: ', filterOptions);        
        const params = Object.assign({}, filterOptions, {
            vatCode: analysisData.vatCode,
            companyId: filterOptions.companyId,
            currency: analysisData.currency || '',
            startAt: pagingOption.startAt ? pagingOption.startAt : DEFAULT_START_AT,
            count: pagingOption.pageSize ? pagingOption.pageSize : DEFAULT_PAGE_SIZE,
        });
        this.props.doLoadAnalysisDetails(params, callback);
    }

    onFetchInvoicesHandler(state, instance) {
        log.debug('onFetchInvoicesHandler(): ', state, instance);
        const pageSize = Object.is(state.pageSize, NaN) ? DEFAULT_PAGE_SIZE : state.pageSize;
        const page = Object.is(state.page, NaN) ? 0 : state.page;
        const startAt = page * pageSize + 1;
        let pagingOption = Object.assign({}, this.state.invoicePagingOption, 
                {
                    pageSize: pageSize, 
                    startAt: startAt,
                    sorted:  state.sorted, 
                    filtered: state.filtered
                }
            );
        //Only make request for non-sorting or non-filter update
        let requestData = _.isEqual(this.state.invoicePagingOption.filtered, state.filtered) && _.isEqual(this.state.invoicePagingOption.sorted, state.sorted);
        if (requestData) {
            this.setState({analysisDetailsLoading: true});
            this.fetchAnalysisDetails(this.state.selectedVatCode || {}, pagingOption, (error, response)=> {
                log.debug('onFetchInvoicesHandler(): error: ', error);
                log.debug('onFetchInvoicesHandler(): response: ', response);            
                if(response) {
                    const totalCount = response.totalCount;
                    pagingOption.pages = Number.isNaN(totalCount) ? DEFAULT_PAGES : (totalCount === 0 ? DEFAULT_PAGES : Math.ceil(totalCount / pageSize));
                    this.setState({invoicePagingOption: pagingOption, analysisDetailsLoading: false});
                }
                else {
                    this.setState({analysisDetailsLoading: false, invoicePagingOption: pagingOption});
                }
            });
        } else {
            this.setState({invoicePagingOption: pagingOption});
        }
    }
    
    onAnalysisClickHandler(state, rowInfo, column, instance) {
        return {
            onClick: (e) => {
              log.debug("onAnalysisClickHandler(): args: ", state, rowInfo, column, instance, e);               
              this.setState( {  selectedVatCode: rowInfo && rowInfo.original }); 
              this.openAnalysisDetailsModal();
            }
        };
    }

    onRedeliverClickHandler() {
        log.debug('onRedeliverClickHandler()');
        const invoices = (this.props.analysisDetails && this.props.analysisDetails.rows) || [];
        log.debug('onRedeliverClickHandler() : invoices: ', invoices);
        const ids = invoices.map( (invoice) => {
            return invoice.documentID;
        });
        log.debug('onRedeliverClickHandler() : ids: ', ids);
        this.setState({
            errorDetails: []
        });
        this.props.doRedeliverTransaction(ids,(error, response) => {
            log.debug('doRedeliverTransaction().callback: ', error, response);
            if (error) {
                let errors =_.isEmpty(error.errorData) ? [] : Array.isArray(error.errorData) ? error.errorData: [error.errorData];
                errors =  errors.map( ( e ) => {
                  return {
                      id: _.replace(`${e.invoiceNumber}`,/\"/g,""),
                      message: `${e.failure}`
                  };
                });
                this.setState({
                  errorDetails: errors
                });
                log.debug('onRedeliverSelectedVATCodesClickHandler(): ERROR performing redeliver: ', errors);
            }
            this.closeAnalysisDetailsModal();
            const apiParams = this.getFilterOptions();   
            this.props.doLoadAnalysis(apiParams);            
        });
    }

    onRedeliverSelectedVATCodesClickHandler() {
        log.debug('onRedeliverSelectedVATCodesClickHandler()');
        log.debug('onRedeliverSelectedVATCodesClickHandler() : invoices: ', this.state.selectedItems);
        const vatCodes = [...this.state.selectedItems];
        const filterOptions = this.getFilterOptions();
        log.debug('onRedeliverSelectedVATCodesClickHandler(): filterOptions: ', filterOptions);        
        const params = Object.assign({}, filterOptions, {
            vatCodes: vatCodes.map( vatCode => vatCode.replace('select-', '') )
        });
        this.setState({
            errorDetails: []
        });
        log.debug('onRedeliverSelectedVATCodesClickHandler() : params: ', params);
        this.props.doRedeliverAllVATCodes(params, (error, response) => {
            log.debug('onRedeliverSelectedVATCodesClickHandler().callback: ', error, response);            
            if (error) {
              let errors =_.isEmpty(error.failures) ? [] : Array.isArray(error.failures) ? error.failures: [error.failures];
              errors =  errors.map( ( e ) => {
                return {
                    id: _.replace(`${e.invoiceNumber}`,/\'/g,""),
                    message: `${e.message}`
                };
              });
              this.setState({
                errorDetails: errors
              });
              log.debug('onRedeliverSelectedVATCodesClickHandler(): ERROR performing redeliver on %j: ', vatCodes, errors);
            }
            this.setState({
                selectedItems: new Set(),
                resetSelection: true
            });
            const apiParams = this.getFilterOptions();   
            this.props.doLoadAnalysis(apiParams);            
        });
    }

    onVATCodesSelectionChange(selection, dataArray=[]) {
        log.debug('onVATCodesSelectionChange() - selection: ', selection);
        log.debug('onVATCodesSelectionChange() - dataArray: ', dataArray);
        if(!selection) {
          return;
        } 
        this.setState({
          selectedItems: new Set(selection),
          resetSelection: false
        });
      }

    render() {
        log.debug('render(): ', this.props);
        let analysisData = (this.props.analysisData && this.props.analysisData.rows) || [];
        let reportFields = (this.props.analysisData && Object.keys(this.props.analysisData.reportFields)) || [];
        const filterOptions = this.getFilterOptions();
        let vatCodSummary = LANGUAGE_CONTROLLER.format(this.props.languages, filterOptions.groupBy && filterOptions.groupBy.languageString);
        vatCodSummary = vatCodSummary + (filterOptions.mainVATCodesOnly === 'true' ? '_'+LANGUAGE_CONTROLLER.format(this.props.languages, 'analysisFilter_mainVATCodesOnly') : '');
        const filename = `${filterOptions.countryCode}_${filterOptions.startDate}_${filterOptions.endDate}_${vatCodSummary}.csv`;   
        const selectedVatCode = this.state.selectedVatCode || {}; 
        const invoices = (this.props.analysisDetails && this.props.analysisDetails.rows) || [];
        const invoicePagingOption = this.state.invoicePagingOption || {};
        let filteredInvoices = invoices;
        if (invoicePagingOption.filtered && invoicePagingOption.filtered.length) {
            filteredInvoices = invoicePagingOption.filtered.reduce((filteredSoFar, nextFilter) => {
                return filteredSoFar.filter(row => {
                    return (row[nextFilter.id] + "").includes(nextFilter.value);
                });
            }, filteredInvoices);
        }
        let sortedInvoices = filteredInvoices;
        if (invoicePagingOption.sorted && invoicePagingOption.sorted.length) {
            sortedInvoices = _.orderBy(
                filteredInvoices,
                invoicePagingOption.sorted.map(sort => {
                    return row => {
                        if (row[sort.id] === null || row[sort.id] === null) {
                            return -Infinity;
                        }
                        return typeof row[sort.id] === "string" ?
                            row[sort.id].toLowerCase() :
                            row[sort.id];
                    };
                }),
                invoicePagingOption.sorted.map(d => (d.desc ? "desc" : "asc"))
            );
        } 
        const disableRedeliverSelectedVATCodesButton = _.isEmpty(this.state.selectedItems);
        const redeliverSelectedVatCodesButtonClasses = cn('primary', {'disabled': disableRedeliverSelectedVATCodesButton});
        
        const currencyFormattedAnalysisData = analysisData.map( item => {
            this.ANALYSIS_DATA_CURRENCY_FIELDS.forEach(currencyFieldName => {
                item[currencyFieldName] = ReactComponentHelper.formatToCurrency(item[currencyFieldName]);
            });
            return item;
        });
        const currencyAndDateFormattedInvoiceData = sortedInvoices.map( item => {
            this.INVOICE_CURRENCY_FIELDS.forEach(currencyFieldName => {
                item[currencyFieldName] = ReactComponentHelper.formatToCurrency(item[currencyFieldName]);
            });
            this.INVOICE_DATE_FIELDS.forEach(dateFieldName => {
                item[dateFieldName] = item[dateFieldName] ? item[dateFieldName].substr(0,10) : item[dateFieldName];
            });
            return item;
        });

        return (
            <s-container>     
                <div className="content-area">
                    <ApplicationMessageWithDetail  messageIds={
                            [   ALL_MESSAGES.TRANSACTION_ACTION_ERROR, 
                                ALL_MESSAGES.VAT_CODES_REDELIVER_ERROR_WITH_VAT_CODES
                            ]
                        } 
                        messageType="applicationError"
                        messageDetails={this.state.errorDetails || []}
                        titleLanguageString={'analysisDetails_RedeliverErrorDetails'}
                    />
                    <ApplicationMessages messageIds={[ALL_MESSAGES.TRANSACTION_ACTION_SUCCESS, ALL_MESSAGES.VAT_CODES_REDELIVER_SUCCESS]} messageType="applicationSuccess" />
                    <h1>{LANGUAGE_CONTROLLER.format(this.props.languages, 'TitleAnalysis')}</h1>
                    <AnalysisFilterPanel countryList={this.props.countryList} filterOptions={filterOptions} onSearchClick={this.filterPanelSearchClickHandler}/>
                    <ExistingAnalysis 
                        data={analysisData} 
                        analysisType={filterOptions.groupBy} 
                        reportFields={reportFields}
                        tableOptions={{ getTdProps: this.onAnalysisClickHandler}}
                        onSelectionChange={this.onVATCodesSelectionChange}
                        resetSelection={this.state.resetSelection}
                    />

                        {analysisData >= 0 ? (
                            <div></div>
                        ): (
                            <div className="mt15 mb20">
                                <button className={redeliverSelectedVatCodesButtonClasses} disabled={disableRedeliverSelectedVATCodesButton} onClick={this.onRedeliverSelectedVATCodesClickHandler} >
                                    {LANGUAGE_CONTROLLER.format(this.props.languages, 'analysisDetails_Redeliver')}
                                </button>
                                <CSVLink
                                    data={sanitizeCsvData(currencyFormattedAnalysisData)}
                                    filename={filename}
                                    headers={this.getHeaders(filterOptions.groupBy)}
                                    className="button secondary">
                                        {LANGUAGE_CONTROLLER.format(this.props.languages, 'global_ExportCurrentTableAsCSV')}
                                </CSVLink>
                            </div>
                        )}
                    <AnalysisDetailsModal 
                        open={this.state.analysisDetailsModalOpen} 
                        titleLanguageString='analysisDetailsModal_analysisDetails' 
                        onCloseClick={this.closeAnalysisDetailsModal} 
                    >
                        <AnalysisDetails
                            loading={this.state.analysisDetailsLoading} 
                            detailsData={selectedVatCode} 
                            invoices={sortedInvoices} 
                            pages={invoicePagingOption.pages || DEFAULT_PAGES}  
                            onFetchInvoices={this.onFetchInvoicesHandler} 
                        />

                        {sortedInvoices >= 0 ? (
                            <div></div>
                        ): (
                            <div className="mt15">
                                <button className='primary' onClick={this.onRedeliverClickHandler} >
                                    {LANGUAGE_CONTROLLER.format(this.props.languages, 'analysisDetails_Redeliver')}
                                </button>
                                <CSVLink
                                    data={sanitizeCsvData(currencyAndDateFormattedInvoiceData)}
                                    filename={`${selectedVatCode.vatCode || ''}_${filename}`} 
                                    headers={this.getInvoiceHeaders()}
                                    className="button secondary"
                                >
                                    {LANGUAGE_CONTROLLER.format(this.props.languages, 'global_ExportCurrentTableAsCSV')}
                                </CSVLink>
                            </div>
                        )}
                    </AnalysisDetailsModal>
                </div>    
            </s-container>
        );
    }

    componentDidMount() {
        log.debug('componentDidMount()');
        log.clearAllDictionaryErrors();
        const apiParams = this.getFilterOptions();   
        if(requiredAPICall(apiParams)) {
            this.props.doLoadAnalysis(apiParams);
        } else {
            this.props.doUnloadAnalysis();
        }
    }
}

function mapStateToProps(state) {
    return {
        languages: state.languages,
        analysisData:  state.analysis && state.analysis.data,
        analysisDetails: state.analysis && state.analysis.analysisDetails,
        countryList : (state.content && state.content.contentData && state.content.contentData.countryList)
    };
}

function mapDispatchersToProps(dispatch) {
    return {
  
        doLoadAnalysis: (params) => {
            log.debug('doLoadAnalysis() - params: ', params);
            const apiParams = {
                companyId: params.companyId,
                countryCode: params.countryCode,
                periodStartDate: params.startDate,
                periodEndDate: params.endDate,
                analysisType: params.groupBy && params.groupBy.value
            };
            dispatch(ANALYSIS_CONTROLLER.doLoad(apiParams));
        },

        doLoadAnalysisDetails: (params, callback) => {
            log.debug('doLoadAnalysisDetails() - params: ', params);
            const apiParams = {
                companyId: params.companyId,
                countryCode: params.countryCode,
                periodStartDate: params.startDate,
                periodEndDate: params.endDate,
                analysisType: params.groupBy && params.groupBy.value,
                startAt: params.startAt,
                count: params.count,
                currency: params.currency || '',
                vatCode: params.vatCode
            };
            dispatch(ANALYSIS_CONTROLLER.doLoadAnalysisDetails(apiParams, callback));
        },
    
        doUnloadAnalysis: () => {
            log.debug('doUnloadAnalysis()');
            dispatch(ANALYSIS_CONTROLLER.doUnload());
        },

        doUnloadAnalysisDetails: () => {
            log.debug('doUnloadAnalysisDetails()');
            dispatch(ANALYSIS_CONTROLLER.doUnloadDetails());
        },

        doRedeliverTransaction: (ids, cb) => {
            log.debug('doTransactionAction: ', ids);
            dispatch(TRANSACTION_EDIT_CONTROLLER.doClearErrors());
            dispatch(TRANSACTIONS_CONTROLLER.doAction('redeliver', ids, 'Redeliver transaction from analysis VAT Code', cb));
        },

        doRedeliverAllVATCodes: (params, callback) => {
            log.debug('doRedeliverAllVATCodes(): params: ', params);
            const apiParams = {
                companyId: params.companyId,
                countryCode: params.countryCode,
                periodStartDate: params.startDate,
                periodEndDate: params.endDate,
                analysisType: params.groupBy && params.groupBy.value,
                vatCodes: params.vatCodes
            };
            log.debug('doRedeliverAllVATCodes(): apiParams: ', apiParams);
            dispatch(ANALYSIS_CONTROLLER.redeliverAllVATCodes(apiParams, callback));
        },

        doClearAllAnalysisErrors: () => {
            dispatch(ANALYSIS_CONTROLLER.clearAllAnalysisErrors());
        }
    }
}

export default connect(mapStateToProps, mapDispatchersToProps)(Analysis);